/** @jsxImportSource @emotion/react */
import {
  List,
  ListItem,
  createStyles,
  useTheme,
  Loader,
  useFormatCurrency,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useAsyncFn, useMount } from 'react-use';
import {
  ErrorPage,
  RenderStatusTooltip,
  MicroArticlePullUpModal,
} from 'components';
import { getTransactionDetails } from './api';
import { useTransactionValue } from './hooks/useTransactionValue';

interface Props {
  transactionId: string;
  redirectBackAction?: () => void;
}

export const TransactionDetails = ({
  transactionId,
  redirectBackAction,
}: Props) => {
  const { formatMessage } = useIntl();
  const { spacing } = useTheme();
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [feeArticleKey, setFeeArticleKey] = useState('article.serviceFee');
  const styles = useStyles();
  const formatCurrency = useFormatCurrency();

  const [{ value, loading, error }, getTransactionData] =
    useAsyncFn(async () => {
      if (transactionId) return getTransactionDetails(transactionId);
    }, [transactionId]);

  useMount(() => {
    getTransactionData();
  });

  const [
    titleId,
    {
      transactionID,
      serviceFee,
      status,
      to,
      from,
      networkFee,
      totalAmount,
      titleAmount,
      titleIcon,
      beneficiaryName,
      originatorName,
      beneficiaryVaspName,
    },
  ] = useTransactionValue(value);

  if (error) {
    return <ErrorPage error={error} tryAgainAction={getTransactionData} />;
  }

  if (loading || !value) return <Loader />;

  const { amount: cryptoAmount, createdAt } = value;

  return (
    <>
      <div css={styles.main}>
        <div css={{ marginTop: spacing.xxs }}>
          <div css={{ marginBottom: spacing.xxs }}>
            <List>
              <ListItem
                icon={titleIcon || cryptoAmount.currencyCode}
                title={formatMessage({ id: titleId })}
                subtitle={createdAt.toLocaleString('default', {
                  timeStyle: 'short',
                  hourCycle: 'h24',
                  dateStyle: 'medium',
                })}
                content={titleAmount || formatCurrency(cryptoAmount)}
              />
            </List>
          </div>
          <List>
            {status && (
              <div css={styles.statusWrapper}>
                <ListItem
                  title={formatMessage({ id: 'status' })}
                  subcontent={status}
                  icon={<RenderStatusTooltip status={status} />}
                />
              </div>
            )}
            {to && (
              <ListItem
                title={formatMessage({ id: 'page.transactionDetails.To' })}
                subcontent={to}
              />
            )}
            {beneficiaryName && (
              <ListItem
                title={formatMessage({ id: 'quickbitBeneficiaryName' })}
                subcontent={beneficiaryName}
              />
            )}
            {beneficiaryVaspName && (
              <ListItem
                title={formatMessage({ id: 'beneficiaryVaspName' })}
                subcontent={beneficiaryVaspName}
              />
            )}
            {originatorName && (
              <ListItem
                title={formatMessage({
                  id: 'page.transactionDetails.originatorName',
                })}
                subcontent={originatorName}
              />
            )}
            {from && (
              <ListItem
                title={formatMessage({ id: 'page.transactionDetails.From' })}
                subcontent={from}
              />
            )}
            {transactionID && (
              <ListItem
                title={formatMessage({ id: 'transactionID' })}
                subcontent={transactionID}
              />
            )}
            {serviceFee && (
              <ListItem
                title={formatMessage({ id: 'serviceFee' })}
                subcontent={serviceFee}
                icon="help"
                onIconClick={() => {
                  setFeeArticleKey('article.serviceFee');
                  setShowFeeModal(true);
                }}
              />
            )}
            {networkFee && (
              <ListItem
                title={formatMessage({ id: 'networkFee' })}
                subcontent={networkFee}
                icon="help"
                onIconClick={() => {
                  setFeeArticleKey('article.networkFee');
                  setShowFeeModal(true);
                }}
              />
            )}
            {totalAmount && (
              <ListItem
                title={formatMessage({
                  id: 'page.transactionDetails.totalAmount',
                })}
                subcontent={totalAmount}
              />
            )}
          </List>
        </div>
      </div>
      {redirectBackAction && (
        <div css={{ marginTop: spacing.xxs, button: { width: '100%' } }}>
          <UtilityButton
            text={formatMessage({
              id: 'redirectButton',
            })}
            onClick={redirectBackAction}
          />
        </div>
      )}
      {showFeeModal && (
        <MicroArticlePullUpModal
          articleKey={feeArticleKey}
          onClose={() => setShowFeeModal(false)}
        />
      )}
    </>
  );
};

const useStyles = createStyles(({ palette, spacing }) => ({
  main: {
    marginTop: spacing.s,
    marginBottom: spacing.s,
    width: '100%',
  },
  action: {
    svg: {
      fill: 'none',
      stroke: palette.grey.x300,
      rotate: '180deg',
    },
  },
  profile: {
    borderRadius: '50%',
    background: palette.black,
    padding: `calc(${spacing.xxxs} / 2)`,
    svg: {
      color: palette.white,
    },
  },
  statusWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));
